import { defineComponent as _defineComponent } from 'vue';
import { normalizeStyle as _normalizeStyle, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, createBlock as _createBlock, withModifiers as _withModifiers, Transition as _Transition, Teleport as _Teleport } from "vue";
const _hoisted_1 = ["aria-busy", "aria-labelledby"];
const _hoisted_2 = { class: "pa-tour__head" };
const _hoisted_3 = ["id"];
const _hoisted_4 = { class: "pa-tour__sr" };
const _hoisted_5 = { class: "pa-tour__body" };
const _hoisted_6 = {
    key: 0,
    class: "pa-tour__kbd"
};
const _hoisted_7 = { class: "pa-tour__foot" };
const _hoisted_8 = {
    class: "pa-tour__dots",
    "aria-hidden": "true"
};
import { computed, nextTick, onBeforeUnmount, ref } from "vue";
import { TOUR_STEPS } from "@/utils/paWorkspaceTour";
import { MOD_KEY } from "@/utils/platformKeys";
const GAP = 14;
const PAD = 8;
const EDGE = 16;
const SCROLL_FOOTER = 72;
const MAX_DEPTH = 7;
const MOVE_MS = 360;
export default /*@__PURE__*/ _defineComponent({
    __name: 'PaTour',
    props: {
        root: {}
    },
    emits: ["update:open"],
    setup(__props, { expose: __expose, emit: __emit }) {
        const props = __props;
        const emit = __emit;
        const EASE = [0.2, 0, 0, 1];
        const open = ref(false);
        const steps = ref([]);
        const index = ref(-1);
        const shown = ref(-1);
        const spot = ref(centerBox());
        const cardPos = ref(null);
        const placed = ref(false);
        const moving = ref(false);
        const cardEl = ref(null);
        const primaryEl = ref(null);
        let run = 0;
        let returnFocus = null;
        const current = computed(() => (shown.value >= 0 ? steps.value[shown.value] : null));
        const variant = ref(null);
        const body = computed(() => {
            const step = current.value;
            if (!step)
                return "";
            return (variant.value && step.variants?.[variant.value]) || step.body;
        });
        const last = computed(() => shown.value === steps.value.length - 1);
        const titleId = computed(() => `pa-tour-${current.value?.id}`);
        const spotStyle = computed(() => ({
            top: `${spot.value.top}px`,
            left: `${spot.value.left}px`,
            width: `${spot.value.width}px`,
            height: `${spot.value.height}px`,
        }));
        const cardStyle = computed(() => cardPos.value ? { top: `${cardPos.value.top}px`, left: `${cardPos.value.left}px` } : {});
        function centerBox() {
            return { top: window.innerHeight / 2, left: window.innerWidth / 2, width: 0, height: 0 };
        }
        function bodyParts(text) {
            return text
                .split(/\[([^\]]+)\]/)
                .map((part, i) => ({ text: i % 2 === 1 && part === "Mod" ? MOD_KEY : part, key: i % 2 === 1 }))
                .filter((part) => part.text);
        }
        function reducedMotion() {
            return window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;
        }
        function target(id) {
            const nodes = (props.root ?? document).querySelectorAll(`[data-tour="${id}"]`);
            return Array.from(nodes).find((el) => el.getClientRects().length > 0) ?? null;
        }
        function start() {
            if (open.value)
                return;
            const visible = TOUR_STEPS.filter((step) => target(step.id));
            if (!visible.length)
                return;
            steps.value = visible;
            returnFocus = document.activeElement;
            open.value = true;
            emit("update:open", true);
            window.addEventListener("keydown", onKey, true);
            window.addEventListener("resize", reposition);
            show(0);
        }
        async function show(i) {
            const ticket = ++run;
            index.value = i;
            moving.value = true;
            const el = target(steps.value[i].id);
            if (!el) {
                if (i < steps.value.length - 1)
                    show(i + 1);
                else
                    close();
                return;
            }
            const plan = scrollPlan(el);
            spot.value = frame(el, plan?.shift ?? 0);
            variant.value = el.dataset.tourVariant ?? null;
            shown.value = i;
            await nextTick();
            place();
            if (plan)
                await scrollPane(plan.pane, plan.to);
            await nextFrame();
            if (ticket !== run || !open.value)
                return;
            const landed = frame(el);
            if (!sameBox(landed, spot.value)) {
                spot.value = landed;
                await nextTick();
                place();
            }
            moving.value = false;
        }
        function scrollPlan(el) {
            const pane = el.closest(".ws__pane");
            if (!pane)
                return null;
            const rect = el.getBoundingClientRect();
            const box = pane.getBoundingClientRect();
            const tall = rect.height > box.height * 0.6;
            const visible = rect.top >= box.top + EDGE &&
                (tall ? rect.top <= box.top + box.height * 0.4 : rect.bottom <= box.bottom - SCROLL_FOOTER);
            if (visible)
                return null;
            const offset = tall ? EDGE * 2 : (box.height - SCROLL_FOOTER - rect.height) / 2;
            const wanted = pane.scrollTop + rect.top - box.top - Math.max(offset, EDGE);
            const to = Math.round(Math.min(Math.max(wanted, 0), pane.scrollHeight - pane.clientHeight));
            return to === pane.scrollTop ? null : { pane, to, shift: to - pane.scrollTop };
        }
        function scrollPane(pane, to) {
            const from = pane.scrollTop;
            if (reducedMotion()) {
                pane.scrollTop = to;
                return Promise.resolve();
            }
            return new Promise((resolve) => {
                const began = performance.now();
                const tick = (now) => {
                    const progress = Math.min((now - began) / MOVE_MS, 1);
                    pane.scrollTop = from + (to - from) * ease(progress);
                    if (progress < 1)
                        requestAnimationFrame(tick);
                    else
                        resolve();
                };
                requestAnimationFrame(tick);
            });
        }
        function ease(x) {
            let t = x;
            for (let i = 0; i < 6; i += 1) {
                const value = bezier(t, EASE[0], EASE[2]) - x;
                const slope = bezierSlope(t, EASE[0], EASE[2]);
                if (Math.abs(value) < 1e-4 || slope === 0)
                    break;
                t -= value / slope;
            }
            return bezier(Math.min(Math.max(t, 0), 1), EASE[1], EASE[3]);
        }
        function bezier(t, a, b) {
            return 3 * (1 - t) * (1 - t) * t * a + 3 * (1 - t) * t * t * b + t * t * t;
        }
        function bezierSlope(t, a, b) {
            return 3 * (1 - t) * (1 - t) * a + 6 * (1 - t) * t * (b - a) + 3 * t * t * (1 - b);
        }
        function nextFrame() {
            return new Promise((resolve) => requestAnimationFrame(() => resolve()));
        }
        function sameBox(a, b) {
            return (Math.abs(a.top - b.top) < 1 &&
                Math.abs(a.left - b.left) < 1 &&
                Math.abs(a.width - b.width) < 1 &&
                Math.abs(a.height - b.height) < 1);
        }
        function frame(el, shift = 0) {
            const rect = el.getBoundingClientRect();
            const vw = window.innerWidth;
            const vh = window.innerHeight;
            const top = Math.min(Math.max(rect.top - shift - PAD, 4), vh - 4);
            const left = Math.max(rect.left - PAD, 4);
            const end = (rect.height > vh ? wholeItemsEnd(el, vh) : rect.bottom + PAD) - shift;
            const bottom = Math.min(Math.max(end, top), vh - 4);
            const right = Math.min(rect.right + PAD, vw - 4);
            return { top, left, width: right - left, height: bottom - top };
        }
        function wholeItemsEnd(el, vh) {
            const top = el.getBoundingClientRect().top;
            const soft = top + vh * 0.45 - PAD;
            const floor = top + vh * 0.2;
            const hard = top + vh * 0.7 - PAD;
            const ends = [];
            const walk = (node, depth) => {
                for (const child of Array.from(node.children)) {
                    const box = child.getBoundingClientRect();
                    if (box.height === 0 || box.top > hard)
                        continue;
                    if (box.bottom > top + 40 && box.bottom <= hard)
                        ends.push(box.bottom);
                    if (depth < MAX_DEPTH)
                        walk(child, depth + 1);
                }
            };
            walk(el, 1);
            const within = ends.filter((end) => end <= soft);
            const fitted = within.length ? Math.max(...within) : 0;
            const later = ends.filter((end) => end >= floor);
            const end = fitted >= floor || !later.length ? fitted || soft : Math.min(...later);
            return end + PAD;
        }
        function place() {
            const card = cardEl.value;
            if (!card)
                return;
            const w = card.offsetWidth;
            const h = card.offsetHeight;
            const vw = window.innerWidth;
            const vh = window.innerHeight;
            const clampX = (x) => Math.min(Math.max(x, EDGE), vw - w - EDGE);
            const clampY = (y) => Math.min(Math.max(y, EDGE), vh - h - EDGE);
            const s = spot.value;
            if (s.top + s.height + GAP + h <= vh - EDGE)
                cardPos.value = { top: s.top + s.height + GAP, left: clampX(s.left) };
            else if (s.top - GAP - h >= EDGE)
                cardPos.value = { top: s.top - GAP - h, left: clampX(s.left) };
            else if (s.left + s.width + GAP + w <= vw - EDGE)
                cardPos.value = { top: clampY(s.top), left: s.left + s.width + GAP };
            else if (s.left - GAP - w >= EDGE)
                cardPos.value = { top: clampY(s.top), left: s.left - GAP - w };
            else
                cardPos.value = { top: clampY(s.top + s.height - h - GAP), left: clampX(s.left + s.width - w - GAP) };
            if (!placed.value)
                requestAnimationFrame(() => requestAnimationFrame(() => (placed.value = open.value)));
            nextTick(() => primaryEl.value?.$el?.focus({ preventScroll: true }));
        }
        function reposition() {
            const step = current.value;
            const el = step ? target(step.id) : null;
            spot.value = el ? frame(el) : centerBox();
            nextTick(place);
        }
        function next() {
            if (index.value < steps.value.length - 1)
                show(index.value + 1);
            else
                close();
        }
        function back() {
            if (index.value > 0)
                show(index.value - 1);
        }
        function close() {
            if (!open.value)
                return;
            run += 1;
            open.value = false;
            moving.value = false;
            window.removeEventListener("keydown", onKey, true);
            window.removeEventListener("resize", reposition);
            emit("update:open", false);
            returnFocus?.focus?.({ preventScroll: true });
        }
        function onAfterLeave() {
            if (open.value)
                return;
            shown.value = -1;
            placed.value = false;
            cardPos.value = null;
        }
        function trapTab(event) {
            const items = Array.from(cardEl.value?.querySelectorAll("button") ?? []);
            if (!items.length)
                return;
            const at = items.indexOf(document.activeElement);
            const nextAt = event.shiftKey ? (at <= 0 ? items.length - 1 : at - 1) : at === items.length - 1 ? 0 : at + 1;
            event.preventDefault();
            items[nextAt].focus();
        }
        function onKey(event) {
            if (!open.value)
                return;
            event.stopPropagation();
            const inCard = cardEl.value?.contains(event.target) ?? false;
            if (event.key === "Escape") {
                event.preventDefault();
                close();
            }
            else if (event.key === "Tab")
                trapTab(event);
            else if (event.key === "ArrowRight" || (event.key === "Enter" && !inCard)) {
                event.preventDefault();
                next();
            }
            else if (event.key === "ArrowLeft") {
                event.preventDefault();
                back();
            }
            else if (!inCard)
                event.preventDefault();
        }
        onBeforeUnmount(close);
        __expose({ start });
        return (_ctx, _cache) => {
            const _component_v_icon = _resolveComponent("v-icon");
            const _component_v_btn = _resolveComponent("v-btn");
            return (_openBlock(), _createBlock(_Teleport, { to: "body" }, [
                _createVNode(_Transition, {
                    name: "pa-tour",
                    onAfterLeave: onAfterLeave
                }, {
                    default: _withCtx(() => [
                        (open.value)
                            ? (_openBlock(), _createElementBlock("div", {
                                key: 0,
                                class: "pa-tour",
                                onWheel: _cache[0] || (_cache[0] = _withModifiers(() => { }, ["prevent"])),
                                onTouchmove: _cache[1] || (_cache[1] = _withModifiers(() => { }, ["prevent"]))
                            }, [
                                _createElementVNode("div", {
                                    class: "pa-tour__spot",
                                    style: _normalizeStyle(spotStyle.value)
                                }, null, 4),
                                _createElementVNode("section", {
                                    ref_key: "cardEl",
                                    ref: cardEl,
                                    class: _normalizeClass(["pa-tour__card", { 'pa-tour__card--ready': placed.value }]),
                                    style: _normalizeStyle(cardStyle.value),
                                    role: "dialog",
                                    "aria-busy": moving.value,
                                    "aria-modal": "true",
                                    "aria-labelledby": titleId.value
                                }, [
                                    (_openBlock(), _createElementBlock("div", {
                                        key: current.value?.id,
                                        class: "pa-tour__content"
                                    }, [
                                        (current.value)
                                            ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                                                _createElementVNode("div", _hoisted_2, [
                                                    _createElementVNode("h2", {
                                                        id: titleId.value,
                                                        class: "pa-tour__title"
                                                    }, [
                                                        _createElementVNode("span", _hoisted_4, "Step " + _toDisplayString(shown.value + 1) + " of " + _toDisplayString(steps.value.length) + ":", 1),
                                                        _createTextVNode(" " + _toDisplayString(current.value.title), 1)
                                                    ], 8, _hoisted_3),
                                                    _createElementVNode("button", {
                                                        type: "button",
                                                        class: "pa-tour__close",
                                                        "aria-label": "Close tour",
                                                        onClick: close
                                                    }, [
                                                        _createVNode(_component_v_icon, { size: "16" }, {
                                                            default: _withCtx(() => [...(_cache[2] || (_cache[2] = [
                                                                    _createTextVNode("mdi-close", -1)
                                                                ]))]),
                                                            _: 1
                                                        })
                                                    ])
                                                ]),
                                                _createElementVNode("p", _hoisted_5, [
                                                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(bodyParts(body.value), (part, i) => {
                                                        return (_openBlock(), _createElementBlock(_Fragment, { key: i }, [
                                                            (part.key)
                                                                ? (_openBlock(), _createElementBlock("kbd", _hoisted_6, _toDisplayString(part.text), 1))
                                                                : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                                                                    _createTextVNode(_toDisplayString(part.text), 1)
                                                                ], 64))
                                                        ], 64));
                                                    }), 128))
                                                ]),
                                                _createElementVNode("div", _hoisted_7, [
                                                    _createElementVNode("span", _hoisted_8, [
                                                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(steps.value, (step, i) => {
                                                            return (_openBlock(), _createElementBlock("span", {
                                                                key: step.id,
                                                                class: _normalizeClass({ 'pa-tour__dot--on': i === shown.value })
                                                            }, null, 2));
                                                        }), 128))
                                                    ]),
                                                    (shown.value > 0)
                                                        ? (_openBlock(), _createBlock(_component_v_btn, {
                                                            key: 0,
                                                            variant: "text",
                                                            rounded: "lg",
                                                            size: "small",
                                                            class: "pa-tour__quiet",
                                                            onClick: back
                                                        }, {
                                                            default: _withCtx(() => [...(_cache[3] || (_cache[3] = [
                                                                    _createTextVNode(" Back ", -1)
                                                                ]))]),
                                                            _: 1
                                                        }))
                                                        : _createCommentVNode("", true),
                                                    _createVNode(_component_v_btn, {
                                                        ref_key: "primaryEl",
                                                        ref: primaryEl,
                                                        color: "primary",
                                                        variant: "flat",
                                                        rounded: "lg",
                                                        size: "small",
                                                        onClick: next
                                                    }, {
                                                        default: _withCtx(() => [
                                                            _createTextVNode(_toDisplayString(last.value ? "Done" : "Next"), 1)
                                                        ]),
                                                        _: 1
                                                    }, 512)
                                                ])
                                            ], 64))
                                            : _createCommentVNode("", true)
                                    ]))
                                ], 14, _hoisted_1)
                            ], 32))
                            : _createCommentVNode("", true)
                    ]),
                    _: 1
                })
            ]));
        };
    }
});
